import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

const api = axios.create({ baseURL: BASE_URL });

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("qk_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("qk_token");
      localStorage.removeItem("qk_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// ── Analytics ────────────────────────────────────────────────────────────────
export const analyticsAPI = {
  get: () => api.get("/admin/analytics"),
};

// ── Orders ───────────────────────────────────────────────────────────────────
export const ordersAPI = {
  list: (params) => api.get("/orders", { params }),
  get: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, status, note) =>
    api.put(`/orders/${id}/status`, { status, note }),
};

// ── Riders ───────────────────────────────────────────────────────────────────
export const ridersAPI = {
  available: () => api.get("/riders/available"),
};

// ── Users ────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list: (params) => api.get("/admin/users", { params }),
  create: (data) => api.post("/admin/users", data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
};

// ── Branches ─────────────────────────────────────────────────────────────────
export const branchesAPI = {
  list: () => api.get("/admin/branches"),
  create: (data) => api.post("/admin/branches", data),
  update: (id, data) => api.put(`/admin/branches/${id}`, data),
  delete: (id) => api.delete(`/admin/branches/${id}`),
};

// ── Promotions ───────────────────────────────────────────────────────────────
export const promotionsAPI = {
  list: (params) => api.get("/promotions", { params }),
  create: (data) => api.post("/promotions", data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  delete: (id) => api.delete(`/promotions/${id}`),
  stats: (id) => api.get(`/promotions/stats/${id}`),
};

// ── Ratings ──────────────────────────────────────────────────────────────────
export const ratingsAPI = {
  leaderboard: () => api.get("/ratings/leaderboard"),
  riderRatings: (id) => api.get(`/ratings/rider/${id}`),
};

// ── B2B ──────────────────────────────────────────────────────────────────────
export const b2bAPI = {
  clients: (params) => api.get("/b2b/clients", { params }),
  approve: (id) => api.put(`/b2b/clients/${id}/approve`),
  suspend: (id) => api.put(`/b2b/clients/${id}/suspend`),
  regenerateKeys: (id) => api.post(`/b2b/clients/${id}/regenerate-keys`),
};

// ── Assignment ────────────────────────────────────────────────────────────────
export const assignmentAPI = {
  unassigned: () => api.get("/admin/orders/unassigned"),
  availableRiders: (orderId) =>
    api.get(`/admin/orders/${orderId}/available-riders`),
  assign: (orderId, rider_id) =>
    api.post(`/admin/orders/${orderId}/assign`, { rider_id }),
};

// ── Financials ────────────────────────────────────────────────────────────────
export const financialsAPI = {
  get: (params) => api.get("/admin/financials", { params }),
  payout: (data) => api.post("/admin/financials/payout", data),
  exportCSV: (params) =>
    api.get("/admin/financials/export", {
      params,
      responseType: "blob",
    }),
};

export const settingsAPI = {
  // Commission
  getCommissionRates: () => api.get("/admin/commission-rates"),
  setRiderRate: (riderId, data) =>
    api.put(`/admin/commission-rates/${riderId}`, data),
  setDefaultRate: (data) => api.put("/admin/commission-rates/default", data),

  // Delivery zones
  getZones: () => api.get("/admin/delivery-zones"),
  createZone: (data) => api.post("/admin/delivery-zones", data),
  updateZone: (id, data) => api.put(`/admin/delivery-zones/${id}`, data),
  deleteZone: (id) => api.delete(`/admin/delivery-zones/${id}`),

  // Promotions (reuses promotions endpoints)
  getPromos: (params) => api.get("/promotions", { params }),
  createPromo: (data) => api.post("/promotions", data),
  updatePromo: (id, data) => api.put(`/promotions/${id}`, data),
  deletePromo: (id) => api.delete(`/promotions/${id}`),
};

// ── Investment Partners ───────────────────────────────────────────────────────
export const investAPI = {
  list:   (params) => api.get("/invest",      { params }),
  getOne: (id)     => api.get(`/invest/${id}`),
};

export default api;